import { ScalarField, ScalarFieldValueReducer, ScalarFieldValueReducers, ValueRange } from './scalar-field';
import { ReducedScalarField, BufferedScalarField, BufferedValueMappedScalarField } from './scalar-field-impl';
import { CacheKey } from './scalar-field';
import { Range as TimeRange } from '../timeline/timeline-common';
import { closeNumber } from '../geometry/geometry';


export interface ScalarFieldSequence {
	isEmpty(): boolean,
	clear(): ScalarFieldSequence,
	removeScalarField(cacheKey: CacheKey): ScalarField,
	addScalarField(source: ScalarField): ScalarFieldSequence,
	setScalarFields(scalarFields: ScalarField[]): ScalarFieldSequence,
	getScalarFields(): ScalarField[],
	getScalarField(time: number | TimeRange, reducer?: ScalarFieldValueReducer): ScalarField,
	timeRange(): TimeRange,
    range(): ValueRange
}

export class ScalarFieldSequenceImpl implements ScalarFieldSequence {
	private scalarFields: ScalarField[];
	private lookupList: Lookup[];

	constructor(private fadeToValue = 0.0, private fadeMillis = 600000) {
		this.scalarFields = [];
		this.lookupList = [];
	}

	public isEmpty(): boolean {
		return (this.scalarFields ? this.scalarFields.length === 0 : true);
	}

	public clear(): ScalarFieldSequence {
		this.scalarFields = [];
		this.lookupList = [];
		return this;
	}

	public removeScalarField(cacheKey: CacheKey): ScalarField {
		const index = this.scalarFields.findIndex(sf => sf.getCacheKey() && sf.getCacheKey().key === cacheKey.key);
		if (index !== -1) {
			const sf = this.scalarFields.splice(index, 1)[0];
			this.updateScalarFieldLookup();
			return sf;
		}
		return undefined;
	}

	public addScalarField(source: ScalarField): ScalarFieldSequence {
		this.scalarFields.push(source);
		this.updateScalarFieldLookup();
		return this;
	}

	public setScalarFields(scalarFields: ScalarField[]): ScalarFieldSequence {
		this.scalarFields = scalarFields;
		this.updateScalarFieldLookup();
		return this;
	}

	public getScalarFields(): ScalarField[] {
		return this.scalarFields;
	}
	public timeRange(): TimeRange {
		if (this.scalarFields && this.scalarFields.length > 0) {
			return {
				start: this.scalarFields[0].timeRange().start,
				end: this.scalarFields[this.scalarFields.length - 1].timeRange().end,
				width: this.scalarFields[this.scalarFields.length - 1].timeRange().end - this.scalarFields[0].timeRange().start
			}
		} else {
			return undefined;
		};
	}
	
	public range(): ValueRange {
		if (this.scalarFields && this.scalarFields.length > 0) {
			return this.scalarFields.reduce((range: ValueRange, scalarField: ScalarField) =>{
				if(( <ScalarField &{ range: () => ValueRange}>scalarField).range){
					const valueRange = ( <ScalarField &{ range: () => ValueRange}>scalarField).range();
					if(valueRange){
						if(!range){
							range = valueRange;
						}else {
							range.min = Math.min(range.min,valueRange.min);
							range.max = Math.max(range.max,valueRange.max);
						}
					}
				}
				return range;
			},undefined);
		} else {
			return undefined;
		};
	}

	public getScalarField(time: number | TimeRange, reducer?: ScalarFieldValueReducer): ScalarField {
		if (time !== undefined && this.lookupList && this.lookupList.length > 0) {
			if (!(<TimeRange>time).start) {
				const millis = <number>time;
				let i = 0;
				let scalarFieldInstant = undefined;

				while (i < this.lookupList.length && !scalarFieldInstant) {
					if (this.lookupList[i].end <= millis) {
						i++;
					} else {
						if (this.lookupList[i].start <= millis) {
							scalarFieldInstant = this.lookupList[i];
							scalarFieldInstant.t = (millis - scalarFieldInstant.start) / (scalarFieldInstant.end - scalarFieldInstant.start);
						} else {
							i = this.lookupList.length;
						}
					}
				}

				if (scalarFieldInstant) {
					let t = (millis - scalarFieldInstant.start) / (scalarFieldInstant.end - scalarFieldInstant.start);
					t = t < 0.0 ? 0.0 : t > 1.0 ? 1.0 : t;
					let tInv = 1.0 - t;

					//                public value( x: number, y: number ): number {
					//                    return ( 1 - this.t) * this.sf0.value( x, y ) + this.t * this.sf1.value( x, y );
					//                }


					let sf0: ScalarField = scalarFieldInstant.startSource !== undefined ? this.scalarFields[scalarFieldInstant.startSource] : undefined;
					let sf1: ScalarField = scalarFieldInstant.endSource !== undefined ? this.scalarFields[scalarFieldInstant.endSource] : undefined;

					if (sf0 === undefined) {
						const valueMapper = closeNumber(t, 0.0) ?
							(_sourceValue: number, _valueIndex: number, _sourceValues: number[]) => this.fadeToValue :
							closeNumber(t, 1.0) ?
								(sourceValue: number, _valueIndex: number, _sourceValues: number[]) => sourceValue :
								(sourceValue: number, _valueIndex: number, _sourceValues: number[]) => tInv * this.fadeToValue + t * sourceValue;

						return new BufferedValueMappedScalarField(<BufferedScalarField>sf1, [], valueMapper);
						//sf0 = this.buildFadeField( sf1, scalarFieldInstant.start );
					} else if (sf1 === undefined) {
						const valueMapper = closeNumber(t, 0.0) ?
							(sourceValue: number, _valueIndex: number, _sourceValues: number[]) => sourceValue :
							closeNumber(t, 1.0) ?
								(_sourceValue: number, _valueIndex: number, _sourceValues: number[]) => this.fadeToValue :
								(sourceValue: number, _valueIndex: number, _sourceValues: number[]) =>  tInv * sourceValue + t * this.fadeToValue;
						return new BufferedValueMappedScalarField(<BufferedScalarField>sf0, [], valueMapper);
						//sf1 = this.buildFadeField( sf0, scalarFieldInstant.end );
					} else {//both are defined
						const valueMapper = closeNumber(t, 0.0) ?
							(sourceValue: number, _valueIndex: number, _sourceValues: number[]) => sourceValue :
							closeNumber(t, 1.0) ?
								(_sourceValue: number, valueIndex: number, _sourceValues: number[]) => (<BufferedScalarField>sf1).getValues()[valueIndex] :
								(sourceValue: number, valueIndex: number, _sourceValues: number[]) =>  tInv * sourceValue + t * (<BufferedScalarField>sf1).getValues()[valueIndex];
						return new BufferedValueMappedScalarField(<BufferedScalarField>sf0, [sf1.getSources()], valueMapper);
					}
				}
			} else {
				const timeRange = <TimeRange>time;
				let scalarFields = this.scalarFields.filter((scalarField) => {
					return scalarField.timeRange().end >= timeRange.start && scalarField.timeRange().start <= timeRange.end;
				});
				if (scalarFields.length > 0) {
					//default reducer for a sequence of scalar fields is 'max'. I.e. a footprint 
					return new ReducedScalarField(scalarFields, reducer ? reducer : ScalarFieldValueReducers.max);
				}
			}
		}
		return undefined;
	}

	private updateScalarFieldLookup() {
		this.lookupList = [];
		if (this.scalarFields != null && this.scalarFields.length > 0) {
			//first sort the sources on their start millis     
			this.scalarFields.sort((a, b) => a.timeRange().start - b.timeRange().start);

			let preliminaryLookUp = this.scalarFields.map((sf, index, list) => {
				const isLastSource = (list.length - 1 === index);
				let nextSource = !isLastSource ? list[index + 1] : undefined;
				let endSource = nextSource && nextSource.timeRange().start === sf.timeRange().end ? index + 1 : index;
				let sourceLookup: Lookup = {
					start: sf.timeRange().start,
					end: sf.timeRange().end,
					startSource: index,
					endSource: endSource
				}
				return sourceLookup;
			});

			this.lookupList = preliminaryLookUp.reduce((accu, lookup, _index, _preliminaryList) => {
				if (!accu.previous) {
					//the very first source so we need to fade-in first
					accu.lookupList.push({
						start: lookup.start - this.fadeMillis,
						end: lookup.start,
						startSource: undefined,
						endSource: lookup.startSource
					});

				} else if (accu.previous.endSource === undefined) {
					//previous source is a fade-out transition so we need to fade in again.
					let actualFadeMillis = (lookup.start - accu.previous.start) / 2;
					let fadeInStart = lookup.start - this.fadeMillis;
					if (actualFadeMillis < this.fadeMillis) {
						//the available transition time is less then required for a full fade-out-in so we correct for a partial fade
						accu.previous.end = accu.previous.start + actualFadeMillis;
						fadeInStart = accu.previous.end;
					} else {
						actualFadeMillis = this.fadeMillis;
					}
					//add the fade-in transition
					accu.lookupList.push({
						start: fadeInStart,
						end: lookup.start,
						startSource: undefined,
						endSource: lookup.startSource

					});
				}
				//add the current sourceLookup to the list of look-ups
				accu.lookupList.push(lookup);
				accu.previous = lookup;
				//check if we need a fade-out
				if (lookup.startSource === lookup.endSource) {
					//the startSource and endSource are equal so a fade-out is needed
					accu.lookupList.push({
						start: lookup.end,
						end: lookup.end + this.fadeMillis,
						startSource: lookup.endSource,
						endSource: undefined
					});
					accu.previous = accu.lookupList[accu.lookupList.length - 1];
				}
				return accu;
			}, { lookupList: <Lookup[]>[], previous: <Lookup>undefined }).lookupList;

		}
	}
}

//internal helper interface
interface Lookup {
	start: number,
	end: number,
	startSource: number,
	endSource: number,
	t?: number
}
